import type React from 'react';
import { useState } from 'react';

import { PaginationResponse } from '@/app/models/pagination';
import { ProjectResponse } from '@/app/models/projects';
import { formatDate } from '@/app/utils/dateUtils';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import { useProjectSearch } from '@/context/ProjectSearchContext';
import { DrawerProjects } from '@/pages/projects';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BarChartIcon from '@mui/icons-material/BarChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Checkbox, CircularProgress, IconButton, Menu, MenuItem, TableCell, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TruncatedTextWithTooltip from '../common/truncatedTextWithTooltip';
import FilePathLink from './generateReport/FilePathLink';

interface ProjectTable1TableProps {
  selectProjects: ProjectResponse[];
  setSelectedProjects: (id: ProjectResponse[]) => void;
  setOpenDrawer: (drawer: DrawerProjects) => void;
  selectOneProject: ProjectResponse;
  setSelectedOneProject: (id: ProjectResponse) => void;
  data: PaginationResponse<ProjectResponse>;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  page: number;
  rowsPerPage: number;
  syncingProjects: string[];
  handlePlanningReportSync: (date: string, code: string) => void;
  generatingProjects: string[];
}

export default function ProjectTable({
  selectProjects,
  setSelectedProjects,
  setOpenDrawer,
  setSelectedOneProject,
  selectOneProject,
  data,
  setPage,
  setRowsPerPage,
  page,
  rowsPerPage,
  syncingProjects,
  handlePlanningReportSync,
  generatingProjects,
}: ProjectTable1TableProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { filters, setFilters } = useProjectSearch();

  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { t } = useTranslation(['projects', 'common']);

  const handleCheckboxChange = (id: ProjectResponse, checked: boolean) => {
    const newSelectedCollaborators = checked
      ? [...selectProjects, id]
      : selectProjects.filter((collabId) => collabId !== id);

    setSelectedProjects(newSelectedCollaborators);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedProjects(data?.content as ProjectResponse[]);
    } else {
      setSelectedProjects([]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, status: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedStatus(status);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrowers = (type: DrawerProjects) => {
    setOpenDrawer(type);
    setAnchorEl(null);
  };

  const handleNavigate = (drawer: DrawerProjects) => {
    handleDrowers(drawer);
    if (selectOneProject) {
      if (drawer === DrawerProjects.DETAILS) {
        navigate(`${selectOneProject.code}`);
      } else if (drawer === DrawerProjects.COST_TRACKING) {
        navigate(`${selectOneProject.code}/cost-tracking`);
      }
    }
  };

  const columns: Column[] = [
    {
      key: 'select',
      label: (
        <Checkbox
          color="secondary"
          sx={{ p: 0 }}
          indeterminate={selectProjects.length > 0 && selectProjects.length < (data?.content.length as number)}
          checked={(data?.content.length as number) > 0 && selectProjects.length === data?.content.length}
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all collaborators' }}
        />
      ),
      width: theme.spacing(6),
    },
    { key: 'project', label: t('common:project'), width: theme.spacing(50) },
    { key: 'branch', label: t('common:branch') },
    { key: 'init', label: t('init'), align: 'center' },
    { key: 'end', label: t('common:end'), align: 'center' },
    { key: 'manager', label: t('manager'), width: theme.spacing(20) },
    { key: 'initialBalance', label: t('initialBalance') },
    { key: 'last.update', label: t('spreadsheet.last.update'), align: 'center' },
    { key: 'sync', label: t('spreadsheet.sync'), align: 'center' },
    { key: 'export', label: t('export'), align: 'center' },
    // { key: 'status', label: t('common:status'), align: 'center' },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (project: ProjectResponse) => {
    const isSelected = selectProjects.includes(project as ProjectResponse);
    const isSyncing = syncingProjects.includes(project.code as string);
    const isGenerating = generatingProjects.includes(project.code as string);

    return (
      <>
        <TableCell sx={{ ...bodyCellSx, padding: 'checkbox' }}>
          <Checkbox
            color="secondary"
            sx={{ p: 0 }}
            checked={isSelected}
            onChange={(e) => handleCheckboxChange(project as ProjectResponse, e.target.checked)}
            inputProps={{ 'aria-labelledby': `project-${project.code}` }}
          />
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <TruncatedTextWithTooltip value={`${project.code} - ${project.name}`} sx={bodyCellSx} size={40} />
        </TableCell>
        <TableCell sx={bodyCellSx}>{project.branchName}</TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {project.plannedInitDate ? formatDate(project.plannedInitDate) : '----'}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {project.plannedEndDate ? formatDate(project.plannedEndDate) : '----'}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip
            value={`${project.manager ? project.manager.name : '----'}`}
            sx={bodyCellSx}
            size={18}
          />
        </TableCell>
        <TableCell sx={{ ...bodyCellSx }}>{formatterMonetaryValue(project.initialBalance)}</TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{project.planningReportLastUpdate}</TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {isSyncing ? <CircularProgress size={20} color="secondary" /> : project.lastPlanningImportDate || ''}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {isGenerating ? <CircularProgress size={20} color="secondary" /> : project.lastGenerationDate || ''}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              handleMenuOpen(e, project.status);
              setSelectedOneProject(project);
            }}
            aria-label="more actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </>
    );
  };

  // Use the project ID as the unique key
  const getRowKey = (project: ProjectResponse) => project.code as string;

  const paginationConfig = {
    page,
    rowsPerPage,
    totalElements: data?.totalElements || 0,
    rowsPerPageOptions: [5, 10, 25],
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
      `${from}-${to} ${t('common:of')} ${count}`,
    labelRowsPerPage: t('common:items.per.page'),
  };

  return (
    <>
      <DataTable
        columns={columns}
        data={data?.content || []}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        pagination={paginationConfig}
        size="small"
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedStatus && (
          <>
            <MenuItem onClick={() => handleDrowers(DrawerProjects.SETTINGS)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountCircleIcon />
                {t('settings')}
              </Box>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setFilters({ ...filters, search: selectOneProject });
                handleDrowers(DrawerProjects.DETAILS);
                navigate(`${selectOneProject.code}`);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListAltIcon />
                {t('details')}
              </Box>
            </MenuItem>
            {selectOneProject.lastPlanningImportDate && (
              <MenuItem onClick={() => handleNavigate(DrawerProjects.COST_TRACKING)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BarChartIcon />
                  {t('cost.tracking')}
                </Box>
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                handlePlanningReportSync(selectOneProject.lastPlanningImportDate, selectOneProject.code as string);
                handleMenuClose();
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutorenewIcon />
                {t('sync.project.spreadsheet')}
              </Box>
            </MenuItem>
            {selectOneProject?.lastGenerationDate && (
              <MenuItem>
                <FilePathLink projectCode={selectOneProject.code as string} />
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </>
  );
}
